import React from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Avatar,
    Typography,
    SelectChangeEvent
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export interface DropdownOption {
    value: string;
    label: string;
    avatar?: string;
    logo?: string;
    subtitle?: string;
    disabled?: boolean;
}

export interface DropdownProps {
    label: string;
    value: string | null;
    options: DropdownOption[];
    onChange: (value: string | null) => void;
    placeholder?: string;
    width?: number | string;
    maxHeight?: number;
    disabled?: boolean;
    size?: 'small' | 'medium';
    renderOption?: (option: DropdownOption) => React.ReactNode;
    emptyValue?: string;
    emptyLabel?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
    label,
    value,
    options,
    onChange,
    placeholder = "Seleccionar...",
    width = 280,
    maxHeight = 320,
    disabled = false,
    size = 'small',
    renderOption,
    emptyValue = '',
    emptyLabel = 'Todos'
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const menuItemHoverBg = alpha(theme.palette.primary.main, isDark ? 0.08 : 0.04);
    const menuItemFocusBg = alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08);

    const handleChange = (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value;
        onChange(newValue === emptyValue ? null : newValue);
    };

    const defaultRenderOption = (option: DropdownOption) => {
        if (option.avatar || option.logo) {
            return (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%'
                }}>
                    <Avatar
                        src={option.avatar || option.logo}
                        alt={option.label}
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            flexShrink: 0
                        }}
                    >
                        {option.label.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                    }}>
                        {option.label}
                        {option.subtitle && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {option.subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        }

        return option.label;
    };

    return (
        <FormControl size={size} sx={{ width }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={value || emptyValue}
                label={label}
                onChange={handleChange}
                disabled={disabled}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight,
                            overflow: 'auto',
                        }
                    },
                    MenuListProps: {
                        sx: {
                            padding: '8px !important',
                            '&.MuiMenuList-root': {
                                padding: '8px !important'
                            }
                        }
                    },
                    slotProps: {
                        paper: {
                            sx: {
                                '& .MuiMenuItem-root': {
                                    borderRadius: '8px',
                                    color: 'text.primary',
                                    '&:hover': {
                                        backgroundColor: menuItemHoverBg,
                                    },
                                    '&:focus': {
                                        backgroundColor: menuItemFocusBg,
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: menuItemFocusBg,
                                        '&:hover': {
                                            backgroundColor: menuItemFocusBg,
                                        },
                                    },
                                }
                            }
                        }
                    }
                }}
            >
                <MenuItem value={emptyValue}>
                    <em>{emptyLabel}</em>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                    >
                        {renderOption ? renderOption(option) : defaultRenderOption(option)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default Dropdown;
